import Logger from '../helpers/logger';
import moment from 'moment';
import jsPDF from 'jspdf';
import { STANDARD_A4_MARGIN, STANDARD_A4_HEIGHT } from './customPDFGenerator';
import { VOTE_ALERTS_INTERVALS } from '../actions/models/entityModel';

export function uniq(arrArg) {
	return arrArg.filter((elem, pos, arr) => {
		return arr.indexOf(elem) === pos;
	});
}

/**
 * performs linear regression on an list of points (sorted chronologically) to determine the trend
 */ 
export function calculateTrendSlope(dataPoints){
	let sumX = 0;
	let sumY = 0;
	for (let i = 0; i < dataPoints.length; i++) {
		sumX += i + 1;
		sumY += dataPoints[i];
	}
	const meanX = sumX / dataPoints.length;
	const meanY = sumY / dataPoints.length;

	let sumSquares = 0;
	let sumDeviations = 0;
	for (let i = 0; i < dataPoints.length; i++) {
		const xDev = (i + 1) - meanX;
		const yDev = dataPoints[i] - meanY;

		sumSquares += Math.pow(xDev, 2);
		sumDeviations += xDev*yDev;
	}

	const regressionSlope = sumDeviations / sumSquares;
	return precisionRound(regressionSlope, 2);	
}

function precisionRound(number, precision) {
	var factor = Math.pow(10, precision);
	return Math.round(number * factor) / factor;
}

/** Crops an image to 16 by 9. Returns a promise with the image in base64 format */
export function applyImageCropAndWatermark(sourceImageFile) {
	return new Promise((resolve, reject) => {
		if(sourceImageFile.type.indexOf("image") === -1){
			reject('Cannot process a file that is not a valid image file.');
		}
		var image = document.createElement('img');
		var logoImg = document.createElement('img');

		logoImg.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="64pt" height="64pt" viewBox="0 0 64 64" version="1.1"><g id="surface1"><path style=" stroke:none;fill-rule:evenodd;fill:rgb(100%,100%,100%);fill-opacity:0.05;" d="M 12.996094 0 L 51.003906 0 C 58.183594 0 64 5.816406 64 12.996094 L 64 51.003906 C 64 58.183594 58.183594 64 51.003906 64 L 12.996094 64 C 5.816406 64 0 58.183594 0 51.003906 L 0 12.996094 C 0 5.816406 5.816406 0 12.996094 0 Z M 12.996094 0 "/><path style=" stroke:none;fill-rule:evenodd;fill:rgb(0%,0%,0%);fill-opacity:0.05;" d="M 14.179688 55.941406 L 14.179688 8.042969 L 25.699219 8.042969 L 25.699219 26.597656 L 25.929688 26.597656 C 27.789062 21.75 31.738281 19.097656 37.25 19.097656 C 45.316406 19.097656 49.796875 24.207031 49.796875 32.738281 L 49.796875 55.941406 L 38.046875 55.941406 L 38.046875 35.292969 C 38.046875 31.210938 36.019531 28.6875 32.136719 28.6875 C 28.1875 28.6875 25.964844 31.511719 25.964844 35.558594 L 25.964844 55.941406 Z M 14.179688 55.941406 "/><path style="fill-rule:evenodd;fill:rgb(0%,0%,0%);fill-opacity:0.05;stroke-width:30;stroke-linecap:butt;stroke-linejoin:miter;stroke:rgb(100%,100%,100%);stroke-opacity:0.05;stroke-miterlimit:4;" d="M 595.972656 1131.984863 C 595.972656 1228.607422 517.622559 1306.95752 421 1306.95752 C 324.377441 1306.95752 246.027344 1228.607422 246.027344 1131.984863 C 246.027344 1035.362305 324.377441 957.012207 421 957.012207 C 517.622559 957.012207 595.972656 1035.362305 595.972656 1131.984863 Z M 595.972656 1131.984863 " transform="matrix(0.042328,0,0,0.042328,14.179894,8.042328)"/></g></svg>';

		var resizedImageDataUrl;

		image.onload = () => {
			var imageWidth = image.width;
			var imageHeight = image.height;
			var aspectRatio = imageWidth / imageHeight;
			var canvas = document.createElement("canvas");
			var ctx = canvas.getContext("2d");

			if (aspectRatio < 1.912) {// we need to crop top and bottom
				canvas.width = imageWidth;
				canvas.height = Math.round(imageWidth / 1.912);
				var vertcrop = Math.round((imageHeight - canvas.height) / 2);
				ctx.drawImage(image, 0, vertcrop, imageWidth, canvas.height, 0, 0, imageWidth, canvas.height);
			} else if (aspectRatio > 1.912) {// we need to crop left and right
				canvas.width = Math.round(imageHeight * 1.912);
				canvas.height = imageHeight;
				var horzcrop = Math.round((imageWidth - canvas.width) / 2);
				ctx.drawImage(image, horzcrop, 0, canvas.width, imageHeight, 0, 0, canvas.width, imageHeight);
			}

			var wthirds = Math.round(Math.abs(imageWidth - 750) / 3); // this is size ratio
			var hthirds = Math.round(Math.abs(imageHeight - 392) / 3);

			if (wthirds > 50) {
				if (imageWidth > 750) {// scale down
					let can2 = document.createElement("canvas");
					let ctx2 = can2.getContext("2d");
					can2.width = 750 + wthirds + wthirds;
					can2.height = 392 + hthirds + hthirds;
					ctx2.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, can2.width, can2.height);

					let can3 = document.createElement("canvas");
					let ctx3 = can3.getContext("2d");
					can3.width = 750 + wthirds;
					can3.height = 392 + hthirds;
					ctx3.drawImage(can2, 0, 0, can2.width, can2.height, 0, 0, can3.width, can3.height);

					let can4 = document.createElement("canvas");
					let ctx4 = can4.getContext("2d");
					ctx4.globalCompositeOperation = "copy";
					can4.width = 750;
					can4.height = 392;
					ctx4.drawImage(can3, 0, 0, can3.width, can3.height, 0, 0, can4.width, can4.height);
					ctx4.drawImage(logoImg, 0, 0, 95, 90, 20, 282, 95, 90);

					resizedImageDataUrl = can4.toDataURL("image/jpeg", 0.7);
					can2 = null;
					can3 = null;
					can4 = null;
				}
				if (imageWidth < 750) {// scale up
					let can2 = document.createElement("canvas");
					let ctx2 = can2.getContext("2d");
					can2.width = 750 - wthirds - wthirds;
					can2.height = 392 - hthirds - hthirds;
					ctx2.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, can2.width, can2.height);

					let can3 = document.createElement("canvas");
					let ctx3 = can3.getContext("2d");
					can3.width = 750 - wthirds;
					can3.height = 392 - hthirds;
					ctx3.drawImage(can2, 0, 0, can2.width, can2.height, 0, 0, can3.width, can3.height);

					let can4 = document.createElement("canvas");
					let ctx4 = can4.getContext("2d");
					ctx4.globalCompositeOperation = "copy";
					can4.width = 750;
					can4.height = 392;
					ctx4.drawImage(can3, 0, 0, can3.width, can3.height, 0, 0, can4.width, can4.height);
					ctx4.drawImage(logoImg, 0, 0, 95, 90, 20, 282, 95, 90);

					resizedImageDataUrl = can4.toDataURL("image/jpeg", 0.7);
					can2 = null;
					can3 = null;
					can4 = null;
				}
			} else {// we just do one scale
				let can2 = document.createElement("canvas");
				let ctx2 = can2.getContext("2d");
				ctx2.globalCompositeOperation = "copy";
				can2.width = 750;
				can2.height = 392;
				ctx2.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, can2.width, can2.height);
				ctx2.drawImage(logoImg, 0, 0, 95, 90, 20, 282, 95, 90);
				resizedImageDataUrl = can2.toDataURL("image/jpeg", 0.7);
				can2 = null;
			}

			canvas = null;
			image = null;

			resolve(resizedImageDataUrl);
		};

		image.onerror = (err) => {
			reject(err.error);
		};

		image.src = sourceImageFile.preview;
	});
}

/** Generates PDF from given values
 * @param {object} pages of shape:
	* @field {object} options carries information about orientation, unit, format of PDF
	* @field {string} fileName the name of the pdf when downloaded
	* @field {number} marginLeft to be used if no margin is specified for any object to be placed on any page
	* @field {number} marginTop as marginLeft
	* @field {arrays} page1, page2, page3, etc, each with objects of shape:
		* { text: { text: string, marginLeft: number, marginTop: number, options: object } }
		* and/or
		* { imageData: { data: string/Image-Element/Canvas-Element/Uint8Array, format: string, marginLeft: number, marginTop: number, width: number, height: number } }
 */
export function downloadPDF(pages) {
    let orientation = 'portrtait';
    let unit = 'px';
    let format = 'a4';

    if (pages && pages.options) {
        const { options } = pages;
        orientation = options.orientation ? options.orientation : orientation;
        unit = options.unit ? options.unit : unit;
        orientation = options.format ? options.format : format;
    }
    const doc = new jsPDF({ orientation, unit, format, compress: true });  
    doc.setFont('helvetica');
    doc.setFontSize(14);

	const pagesCount = Object.keys(pages).filter(key => key.includes('page')).length;
    const dateNow = new Date();

	for ( let i = 1; i <= pagesCount; i++ ) {
		const elementsOnPage = pages[`page${i}`];
		const footerNote = `Date: ${moment(dateNow).format('MMM D YYYY')}. Page ${i} of ${pagesCount}.`;

		elementsOnPage.forEach(( element ) => {
            const elementTypesHolder = Object.keys(element); // has to be only one type per element
            const elementType = `${Object.keys(element)[0]}`;

			if ( elementTypesHolder.length === 1 ) {
				if ( elementType === 'text' ) {
					const { text, marginLeft, marginTop, options } = element.text;
                    doc.text(text, marginLeft || pages.marginLeft || null, marginTop || pages.marginTop || null, options || null);
                    
				} else if ( elementType === 'imageData' ) {
                    const { data, format, marginLeft, marginTop, width, height } = element.imageData;
					doc.addImage(data, format || null, marginLeft || pages.marginLeft || null, marginTop || pages.marginTop || null, width || null, height || null);
				} 
			}
		});

		doc.text(footerNote,
			STANDARD_A4_MARGIN * 3,
			STANDARD_A4_HEIGHT - (STANDARD_A4_MARGIN / 2),
			{
				charSpace: 0,
				lineHeightFactor: '0.6' 
			}
		);
		
		if ( i + 1 <= pagesCount ) {
			doc.addPage();
		}
	}

	return doc.save(`${pages.fileName ? `${pages.fileName}` : 'Capture_view'}.pdf`, {returnPromise: true});
}

// fits an image into maxWidth by maxHeight
export function applyImageFit(sourceImageFile, maxWidth, maxHeight, scalingQuality = 0.7) {
	
	return new Promise((resolve, reject) => {
		if(sourceImageFile.type.indexOf('image') === -1) {
			reject('Cannot process a file that is not a valid image file.');
		}
		let image = document.createElement('img');
		let resizedImageDataUrl;
		
		image.onload = () => {
			const imageWidth = image.width;
			const imageHeight = image.height;

			let canvas = document.createElement('canvas');
			canvas.width = image.width;
			canvas.height = image.height;

			// use full canvas for full image as a start
			canvas.getContext('2d').drawImage(image, 0, 0, imageWidth, imageHeight, 0, 0, canvas.width, canvas.height);

			do {
				canvas = resizeCanvas(canvas, maxWidth, maxHeight);
			} 
			while (canvas.width > maxWidth && canvas.height > maxHeight);
			
			resizedImageDataUrl = canvas.toDataURL('image/jpeg', scalingQuality);

			canvas = null;
			image = null;
			
			resolve(resizedImageDataUrl);
		};
		image.src = sourceImageFile.preview;

		image.onerror = (err) => {
			reject(err.error);
		};
	});
}

function resizeCanvas(canvas, maxWidth, maxHeight) {
	const height = canvas.height;
	const width = canvas.width;
	const fitRatio = Math.min(maxWidth / width, maxHeight / height);

	// use this as a smaller step to reduce the size of the canvas iteratively
	const minDownscalingRatio = 3 / 4;

    const resizedCanvas = document.createElement('canvas');

	if (minDownscalingRatio > fitRatio) {
		resizedCanvas.width = width * minDownscalingRatio;
		resizedCanvas.height = height * minDownscalingRatio;
	} else {
		resizedCanvas.width = width * fitRatio;
		resizedCanvas.height = height * fitRatio;
	}

	resizedCanvas.getContext('2d').drawImage(canvas, 0, 0, width, height, 0, 0, resizedCanvas.width, resizedCanvas.height);

	return resizedCanvas;
}

export function getBase64Data(sourceImageFile) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const base64ImageData = reader.result;
			resolve(base64ImageData);
		};
		reader.onerror = (err) => { Logger.error('Cannot process file'); reject(err); };
		reader.readAsDataURL(sourceImageFile);
	});
}

export function createTopicHash(topicName) {
	if(!topicName){
		Logger.error('Cannot create hash from empty name!');
	}
	return topicName.toLowerCase().replace(/\s+/g, '').replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');
}

function getCurrentDomainByEnv() {
	const env = process.env.REACT_APP_DEPLOY_ENV || 'localdev';
	let domain;
	
	switch (env) {
		case 'production' : {
			domain = { domainName: 'holler.live', templatesDomainName: 'm.holler.live/m' };
			break;
		}
		case 'staging' : {
			domain = { domainName: 'staging.holler.live', templatesDomainName: 'ms.holler.live/m' };
			break;
		}
		case 'localdev' : {
			domain = {
				domainName: 'dashlocalv.holler.live',
				templatesDomainName: 'ms.holler.live/m' // this is somewhat inapplicable because on local templates arrive at https://dashlocal.holler.live:3002/m/topichash.html
			};
			break;
		}
		default : {
			domain = { domainName: 'holler.live', templatesDomainName: 'm.holler.live/m' };
		}
	}

	return domain;
}

export function getTopicPreviewUrl(title) {
	const domain = getCurrentDomainByEnv();
	const urlPreview = createTopicHash(title);
	return `https://${domain.domainName}/#${urlPreview}`;
}

export function getVoterPreviewUrl(title, sourcesCsv = null) {
	const domain = getCurrentDomainByEnv();
	const topicHash = createTopicHash(title);
	let topicUrl = `https://${domain.templatesDomainName}/${topicHash}.html`;
	if (sourcesCsv && sourcesCsv.length > 0) {
		const sourcesGetParams = sourcesCsv.replace(/,/gi, '&s=');
		topicUrl = `${topicUrl}?s=${sourcesGetParams}`;
	}
	return topicUrl;
}

export function getValueByFieldPath(obj = {}, fieldPath) {
	return fieldPath.split('.').reduce((o, i) => {
		return o[i] !== undefined ? o[i] : {};
	}, obj);
}

export function isOnMobile(){
	return document.body.clientWidth < 769;
}

export function isOnLargeMobile(){
	return document.body.clientWidth >= 769 && document.body.clientWidth <= 1366;
}

export function getElapsedTime(date, includeDayOfWeek = true, useShortDateFormat = false) {
	if (!date) {
		Logger.error('Cannot get elapsed time without a date!');
		return '';
	}
	
	const now = moment(new Date());
	const dateVoted = moment(date.toString());
	const hoursSinceVote = moment.duration(now.diff(dateVoted)).asHours();
	
	// From 22 to 35 hours moment.fromNow() starts to display "a day ago"
	if (hoursSinceVote >= 22) {
		return moment(date.toString()).format(includeDayOfWeek ? "ddd, Do MMM YYYY" : useShortDateFormat ? "DD/MM/YY" : "Do MMM YYYY");
	} else {
		return moment(date.toString()).fromNow();
	}
}

export const getAlertsLabels = () => {
	const btns = [];
	VOTE_ALERTS_INTERVALS.forEach(interval => {
		let name;

		switch (interval) {
			case 60:
				name = "hourly";
				break;
			case 1440:
				name = "daily";
				break;
			case 10080:
				name = "weekly";
				break;
			default:
				name = `every ${interval} min`;
		}

		btns.push({ name, value: interval });
	});
	return btns;
};

/** isRequired proptype HOC for a handler that checks a boolean flag
 * @param{string} flagPropName */
export function isRequiredFuncIfNotSetFlag(flagPropName) {
    return (props, propName, component) => {
        if ((!props.hasOwnProperty(flagPropName) || props[flagPropName] === false) && !props.hasOwnProperty(propName)) {
            return new Error(`The prop '${propName}' is marked as required in '${component}', but its value is not set.`);
        }
        if (typeof props[propName] !== 'function') {
            return new Error(
                `The prop '${propName}' in '${component}' is of type '${typeof props[
                    propName
                ]}', which is not a handler.'`
            );
        }
    };
} 